import React, {useCallback} from 'react'
import * as PropTypes from 'prop-types'
import {path} from 'ramda'
import {
  ButtonAlert,
  ButtonLink,
  PencilIcon,
  ButtonSecondary,
  UploadFileIcon,
  Row,
  Col,
  Small,
  CsvFileIcon,
} from '@modifi/ui'
import {FormattedDate, FormattedNumber, useTranslation} from '@modifi/plugin-i18n'
import {DocumentType, ProductType} from '@modifi/enums'
import {white, yellow} from '@modifi/colors'
import {DetailsTbody, DetailsTd, DetailsTh, DetailsThead} from '../../../../styles/common'
import {MATCH_GROUP_PAYMENT, matchTypes, matchGroups} from '../../../match/constants'
import BankCard from '../../../shared/components/bank-card'
import {BANK_TYPE_SENDER} from '../../../shared/constants'
import {accountConfigShape, accountTypeMap, productTypeMap} from '../../constants'
import {customerBankMap, tradePartnerBankMap} from '../../config'
import {SourceHighlighterContainer} from '../../../shared/components/highlights'
import {DIALOG_NAME as DIALOG_DELETE_TRANSACTION} from '../../dialogs/delete-transaction-dialog'
import {DIALOG_NAME as DIALOG_TAG_TRANSACTION} from '../../dialogs/edit-transaction-dialog'
import {DIALOG_NAME as DIALOG_MANAGE_DOCUMENTS} from '../../dialogs/manage-documents-dialog'
import {DIALOG_NAME as DIALOG_PASS_THROUGH_TRANSACTION} from '../../dialogs/pass-through-transaction-dialog'
import {DIALOG_NAME as DIALOG_BULK_MATCHING} from '../../dialogs/bulk-matching-dialog'
import {TransactionStatusTag} from '../../../../components/transaction-status-tag'
import {StyledComment, TitleWrapper, NameTag} from './styles'

const TransactionBox = props => {
  const {
    amount,
    currency,
    valueDate,
    purpose,
    accountConfig: {
      productType,
      accountTypes,
      refinancingProvider1,
      refinancingProvider2,
      bankProvider,
    },
    matchType,
    matchGroup,
    isSticky,
    inverseBackground,
    showDialog,
    id,
    tag,
    transactionDocumentRefs,
    fetchTransaction,
    isPassThrough,
  } = props

  const {t} = useTranslation('moneta')

  const handleSubmit = useCallback(async () => {
    await fetchTransaction(id)
  }, [fetchTransaction, id])

  return (
    <>
      <DetailsThead inverseBackground={inverseBackground} isSticky={isSticky}>
        <tr>
          <DetailsTh colSpan={matchGroup === MATCH_GROUP_PAYMENT ? 5 : 5}>
            {t('unmatched-transaction-box.header')}{' '}
            {isPassThrough && (
              <NameTag color={yellow}>
                <Small color={white}>{t('unmatched-transaction-box.pass-through.label')}</Small>
              </NameTag>
            )}
          </DetailsTh>
          {!inverseBackground && (
            <DetailsTh align="right" colSpan={matchGroup === MATCH_GROUP_PAYMENT ? 6 : 3}>
              <Row justifyContent="end">
                {productType === ProductType.SELLER_FINANCING && (
                  <Col auto>
                    <ButtonSecondary
                      data-ident="button"
                      data-target="bulk-matching"
                      data-type="link"
                      onClick={() => showDialog(DIALOG_BULK_MATCHING, {id, isPassThrough})}
                      small
                      icon
                      block
                      inverse
                    >
                      <CsvFileIcon height={16} width={16} />
                      <span>{t('unmatched-transaction-details.page.bulk-matching.cta')}</span>
                    </ButtonSecondary>
                  </Col>
                )}
                <Col auto>
                  <ButtonSecondary
                    data-ident="button"
                    data-target="manage-documents"
                    data-type="link"
                    onClick={() =>
                      showDialog(DIALOG_MANAGE_DOCUMENTS, {
                        documents: transactionDocumentRefs,
                        availableDocumentTypes: DocumentType.REMITTANCE_FILE,
                        transactionId: id,
                        onSubmit: handleSubmit,
                      })
                    }
                    small
                    icon
                    block
                    inverse
                  >
                    <UploadFileIcon height={16} width={16} />
                    <span>
                      {t(
                        `unmatched-transaction-details.page.${
                          transactionDocumentRefs?.length > 0 ? 'manage' : 'upload'
                        }.cta`
                      )}
                    </span>
                  </ButtonSecondary>
                </Col>
                {productType === ProductType.SELLER_FINANCING && (
                  <Col auto>
                    <ButtonSecondary
                      data-ident="button"
                      data-target="pass-through"
                      data-type="link"
                      onClick={() =>
                        showDialog(DIALOG_PASS_THROUGH_TRANSACTION, {id, isPassThrough})
                      }
                      small
                      block
                    >
                      {t(
                        `unmatched-transaction-details.page.${
                          isPassThrough ? 'unmark' : 'mark'
                        }-pass-through.cta`
                      )}
                    </ButtonSecondary>
                  </Col>
                )}
                <Col auto>
                  <ButtonAlert
                    data-ident="button"
                    data-target="delete-transaction"
                    data-type="link"
                    onClick={() => showDialog(DIALOG_DELETE_TRANSACTION, {id})}
                    small
                    block
                  >
                    {t('unmatched-transaction-details.page.delete.cta')}
                  </ButtonAlert>
                </Col>
              </Row>
            </DetailsTh>
          )}
        </tr>
      </DetailsThead>
      <DetailsTbody inverseBackground={inverseBackground} isSticky={isSticky}>
        <tr>
          <DetailsTd colSpan={matchGroup === MATCH_GROUP_PAYMENT ? 3 : 1}>
            <strong>{t('unmatched-transaction-box.purpose.title')}</strong>
            <SourceHighlighterContainer>{purpose}</SourceHighlighterContainer>
          </DetailsTd>
          <DetailsTd>
            <BankCard
              bankType={customerBankMap[matchType]}
              bankName={
                customerBankMap[matchType] === BANK_TYPE_SENDER
                  ? path([`${customerBankMap[matchType]}BankName`], props)
                  : null
              }
              bankAccountHolder={path([`${customerBankMap[matchType]}BankAccountHolder`], props)}
              bankCode={path([`${customerBankMap[matchType]}BankCode`], props)}
              bankAccountNumber={path([`${customerBankMap[matchType]}BankAccountNumber`], props)}
            />
          </DetailsTd>
          <DetailsTd>
            <BankCard
              bankType={tradePartnerBankMap[matchType]}
              bankName={
                tradePartnerBankMap[matchType] === BANK_TYPE_SENDER
                  ? path([`${tradePartnerBankMap[matchType]}BankName`], props)
                  : null
              }
              bankAccountHolder={path(
                [`${tradePartnerBankMap[matchType]}BankAccountHolder`],
                props
              )}
              bankCode={path([`${tradePartnerBankMap[matchType]}BankCode`], props)}
              bankAccountNumber={path(
                [`${tradePartnerBankMap[matchType]}BankAccountNumber`],
                props
              )}
              refinancingProvider1={refinancingProvider1}
              refinancingProvider2={refinancingProvider2}
              bankProvider={bankProvider}
              productType={productType}
            />
          </DetailsTd>
          <DetailsTd>
            <strong>{t('unmatched-transaction-box.account.title')}</strong>
            <p>
              {productTypeMap[productType]}&nbsp;
              {accountTypes.length === 1 && accountTypeMap[accountTypes[0]]}
            </p>
          </DetailsTd>
          <DetailsTd colSpan={matchGroup === MATCH_GROUP_PAYMENT ? 1 : 1}>
            <strong>{t('unmatched-transaction-box.value-date.title')}</strong>
            <p>
              <FormattedDate>{valueDate}</FormattedDate>
            </p>
          </DetailsTd>
          <DetailsTd colSpan={matchGroup === MATCH_GROUP_PAYMENT ? 2 : 1}>
            <TitleWrapper>
              <strong>{t('unmatched-transaction-box.transaction-status.title')}</strong>
              {!inverseBackground && (
                <ButtonLink
                  data-target="edit-order-amount"
                  iconOnly
                  transparent
                  onClick={() => showDialog(DIALOG_TAG_TRANSACTION, {tag, id})}
                  small
                >
                  <PencilIcon height={24} width={24} />
                </ButtonLink>
              )}
            </TitleWrapper>
            <TransactionStatusTag status={tag?.transactionCategory} />
            <StyledComment>{tag?.additionalComment}</StyledComment>
          </DetailsTd>
          <DetailsTd>
            <strong>{t('unmatched-transaction-box.amount.title')}</strong>
            <p>
              {amount < 0 && '-'}
              <FormattedNumber currency={currency}>{Math.abs(amount)}</FormattedNumber>
            </p>
          </DetailsTd>
          <td aria-hidden>&nbsp;</td>
        </tr>
      </DetailsTbody>
    </>
  )
}

TransactionBox.propTypes = {
  amount: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  valueDate: PropTypes.string.isRequired,
  purpose: PropTypes.string.isRequired,
  accountConfig: PropTypes.shape(accountConfigShape).isRequired,
  matchType: PropTypes.oneOf(matchTypes),
  matchGroup: PropTypes.oneOf(matchGroups),
  inverseBackground: PropTypes.bool,
  isSticky: PropTypes.bool,
  showDialog: PropTypes.func.isRequired,
  id: PropTypes.number,
  tag: PropTypes.shape({
    transactionCategory: PropTypes.string,
    additionalComment: PropTypes.string,
  }),
  transactionDocumentRefs: PropTypes.arrayOf(
    PropTypes.shape({
      documentType: PropTypes.string.isRequired,
    }).isRequired
  ),
  fetchTransaction: PropTypes.func.isRequired,
  isPassThrough: PropTypes.bool.isRequired,
}

TransactionBox.defaultProps = {
  // setting defaults because on split transaction dialog
  // we are not passing matchType and matchGroup
  matchType: matchTypes[0],
  matchGroup: matchGroups[0],
  inverseBackground: false,
  isSticky: false,
  id: undefined,
  tag: undefined,
  transactionDocumentRefs: [],
}

export default TransactionBox
